import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function AuthRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const method = searchParams.get("method") || "email";
  const redirectUrl = searchParams.get("redirect") || "/dashboard";

  const [mode, setMode] = useState("register"); // register, login
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  // Handle Apple Sign In
  useEffect(() => {
    if (method === "apple") {
      initAppleSignIn();
    }
  }, [method]);

  const initAppleSignIn = async () => {
    // Apple Sign In requires their JS SDK
    // For now, show a message that it's being set up
    toast.info("Apple Sign In wird eingerichtet...");

    // Load Apple JS SDK
    const script = document.createElement("script");
    script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.onload = () => {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: "com.mokiplaner.auth", // Replace with actual Apple Service ID
          scope: "name email",
          redirectURI: `${window.location.origin}/auth/apple/callback`,
          usePopup: true
        });
      }
    };
    document.head.appendChild(script);
  };

  const handleAppleSignIn = async () => {
    try {
      if (!window.AppleID) {
        toast.error("Apple Sign In nicht verfügbar");
        return;
      }

      const response = await window.AppleID.auth.signIn();

      // Send to backend
      const result = await axios.post(`${API}/auth/apple/callback`, {
        id_token: response.authorization.id_token,
        authorization_code: response.authorization.code,
        user: response.user
      }, { withCredentials: true });

      if (result.data.success) {
        toast.success("Erfolgreich angemeldet!");
        navigate(redirectUrl);
      }
    } catch (error) {
      console.error("Apple Sign In error:", error);
      toast.error("Apple-Anmeldung fehlgeschlagen");
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register/email`, {
        name: formData.name,
        email: formData.email,
        password: formData.password
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success("Registrierung erfolgreich!");
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Registrierung fehlgeschlagen";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!formData.email.trim() || !formData.password) {
      toast.error("Bitte E-Mail und Passwort eingeben");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login/email`, {
        email: formData.email,
        password: formData.password
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success("Erfolgreich angemeldet!");
        navigate(redirectUrl);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Anmeldung fehlgeschlagen";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `/login?redirect=${redirectUrl}`;
  };

  // Apple Sign In View
  if (method === "apple") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <button
              onClick={() => navigate(-1)}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Zurück
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Mit Apple anmelden</h1>
              <p className="text-gray-600 mt-2">Sichere Anmeldung mit deiner Apple ID</p>
            </div>

            <Button
              onClick={handleAppleSignIn}
              className="w-full h-14 rounded-xl bg-black hover:bg-gray-900 text-white text-lg"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Mit Apple fortfahren
            </Button>

            <p className="text-center text-sm text-gray-500 mt-6">
              Apple Sign In erfordert ein Apple-Entwicklerkonto.
              <br />
              <button onClick={handleGoogleLogin} className="text-indigo-600 hover:underline mt-2">
                Stattdessen mit Google anmelden
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Email Sign In View
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "register" ? "Konto erstellen" : "Anmelden"}
            </h1>
            <p className="text-gray-600 mt-2">
              {mode === "register"
                ? "Registriere dich mit deiner E-Mail-Adresse"
                : "Melde dich mit deinem Konto an"}
            </p>
          </div>

          <form onSubmit={mode === "register" ? handleEmailRegister : handleEmailLogin} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Dein Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="deine@email.de"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Passwort</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {mode === "register" && (
              <div>
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-lg mt-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : mode === "register" ? (
                "Registrieren"
              ) : (
                "Anmelden"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-indigo-600 hover:underline"
            >
              {mode === "register"
                ? "Bereits ein Konto? Anmelden"
                : "Noch kein Konto? Registrieren"}
            </button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">oder</span>
            </div>
          </div>

          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-12 rounded-xl"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Mit Google fortfahren
          </Button>
        </div>
      </div>
    </div>
  );
}
